import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Plus } from "lucide-react";
import {
  servicerInstructionsApi,
  downloadBlob,
  type ServicerInstructionListItem,
} from "@/api/servicerInstructions";
import { Badge, Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { servicerInstructionKeys } from "./servicerInstructionKeys";

/**
 * Payment System v3.0 (WS1) — Servicer Instruction Report list. The INVERSE of
 * the PIR list: these reports cover the disbursement lines the servicer
 * executes directly.
 */
export function ServicerInstructionsListPage() {
  const { hasPermission } = useAuth();
  const canGenerate = hasPermission("ServicerInstruction.Generate");
  const { dateTime } = useDateFormatter();

  const list = useQuery({
    queryKey: servicerInstructionKeys.list,
    queryFn: () => servicerInstructionsApi.list(),
  });

  const columns: Column<ServicerInstructionListItem>[] = [
    {
      key: "reportNumber",
      header: "Report #",
      render: (r) => (
        <Link
          to={`/servicer-instructions/${r.id}`}
          className="font-semibold hover:text-gold-shadow"
        >
          {r.reportNumber}
        </Link>
      ),
    },
    {
      key: "scope",
      header: "Scope",
      render: (r) => {
        if (r.loanId) return <Badge status="info">Loan</Badge>;
        if (r.poolId) return <Badge status="info">Pool</Badge>;
        return <Badge status="info">All Live</Badge>;
      },
    },
    {
      key: "range",
      header: "Date Range",
      render: (r) =>
        r.dateRangeStart || r.dateRangeEnd
          ? `${r.dateRangeStart ?? "…"} → ${r.dateRangeEnd ?? "…"}`
          : "—",
    },
    { key: "generatedBy", header: "Generated By", render: (r) => r.generatedByUserId },
    {
      key: "generatedAt",
      header: "Generated At",
      render: (r) => dateTime(r.generatedAt),
    },
    {
      key: "totals",
      header: "Total Disbursements / Amount",
      render: (r) =>
        `${r.totalDisbursements} • $${r.totalAmount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <div className="flex items-center gap-space-2 justify-end">
          <button
            type="button"
            className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
            aria-label={`Download ${r.reportNumber} CSV`}
            onClick={async () => {
              const blob = await servicerInstructionsApi.downloadCsv(r.id);
              downloadBlob(blob, `${r.reportNumber}.csv`);
            }}
          >
            <Download size={16} aria-hidden /> CSV
          </button>
          <button
            type="button"
            className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
            aria-label={`Download ${r.reportNumber} PDF`}
            onClick={async () => {
              const blob = await servicerInstructionsApi.downloadPdf(r.id);
              downloadBlob(blob, `${r.reportNumber}.pdf`);
            }}
          >
            <Download size={16} aria-hidden /> PDF
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Servicer Instruction Reports"
        description="Reports listing every live disbursement the servicer executes directly, batched per period. Generating a report seals the included disbursements so they are not re-emitted."
        actions={
          canGenerate && (
            <Link to="/servicer-instructions/new">
              <Button>
                <Plus size={16} aria-hidden /> New report
              </Button>
            </Link>
          )
        }
      />
      <DataTable
        columns={columns}
        rows={list.data ?? []}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage="No servicer instruction reports have been generated yet."
        rowKey={(r) => r.id}
      />
    </>
  );
}
